import React from 'react';
import { Check, Star, Users, X } from 'lucide-react';

const BASE_FEATURES = [
  'AI voice + chat agents live in minutes',
  'Call Analytics',
  'Sub-account re-billing',
  'Native LeadConnector sync',
  'AI Wizard — auto-builds agents, knowledge bases & widgets',
  'Client Onboarding Engine — new accounts on autopilot',
  'Books appointments 24/7 — calls, SMS, WhatsApp, Instagram & Facebook',
];

// Plan differentiators — BYOK unlocks on Starter+, full white-label on Agency+.
const featuresFor = (planId) => {
  const extras = [];
  if (planId === 'agency' || planId === 'pro') extras.push('Full white-label');
  if (planId !== 'launchpad') extras.push('BYOK — bring your own API keys');
  return [
    ...extras.map((label) => ({ label, highlight: true })),
    ...BASE_FEATURES.map((label) => ({ label, highlight: false })),
  ];
};

const PLANS = [
  {
    id: 'launchpad', name: 'Free', monthly: 0, subAccounts: '1', subLabel: 'Sub-Account',
    badge: null, badgeColor: '', glowClass: '', cardBg: 'bg-[#0f1017]', borderClass: 'border-white/[0.06]',
    checkColor: 'text-gray-400', subPillClass: 'bg-white/[0.05] border-white/10 text-gray-200',
  },
  {
    id: 'starter', name: 'Starter', monthly: 197, subAccounts: '3', subLabel: 'Sub-Accounts',
    badge: null, badgeColor: '', glowClass: '', cardBg: 'bg-[#0f1017]', borderClass: 'border-white/[0.06]',
    checkColor: 'text-gray-400', subPillClass: 'bg-white/[0.05] border-white/10 text-gray-200',
  },
  {
    id: 'agency', name: 'Agency', monthly: 397, subAccounts: '10', subLabel: 'Sub-Accounts',
    badge: 'Most Popular', badgeColor: 'bg-gradient-to-r from-teal-400 to-cyan-400 text-black',
    glowClass: 'ring-1 ring-teal-400/30 shadow-[0_0_30px_rgba(20,184,166,0.15),0_0_60px_rgba(20,184,166,0.05)]',
    cardBg: 'bg-gradient-to-b from-teal-500/[0.04] to-[#0f1017]', borderClass: 'border-teal-400/20',
    checkColor: 'text-teal-400', subPillClass: 'bg-teal-400/10 border-teal-400/25 text-teal-300',
  },
  {
    id: 'pro', name: 'Pro', monthly: 897, subAccounts: 'Unlimited', subLabel: 'Sub-Accounts',
    badge: 'Best Value', badgeColor: 'bg-gradient-to-r from-violet-400 to-purple-500 text-white',
    glowClass: 'ring-1 ring-violet-400/30 shadow-[0_0_30px_rgba(139,92,246,0.15),0_0_60px_rgba(139,92,246,0.05)]',
    cardBg: 'bg-gradient-to-b from-violet-500/[0.04] to-[#0f1017]', borderClass: 'border-violet-400/20',
    checkColor: 'text-violet-400', subPillClass: 'bg-violet-400/10 border-violet-400/25 text-violet-300',
  },
];

export default function PlanCompareModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="vip-plan-compare-modal"
    >
      <div className="min-h-full flex items-start justify-center p-4 sm:p-8">
        <div
          className="relative w-full max-w-6xl rounded-3xl bg-[#0a0b10] border border-white/10 px-5 py-8 sm:p-10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="vip-plan-compare-close"
          >
            <X size={20} />
          </button>

          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-display font-semibold text-white mb-3">
              Simple, Transparent Pricing
            </h2>
            <p className="text-base text-gray-400 max-w-xl mx-auto">
              Same core platform and usage rates on every plan. Higher plans unlock BYOK, full white-label, and more client sub-accounts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLANS.map((plan) => (
              <div
                key={plan.id}
                data-testid={`compare-plan-${plan.id}`}
                className={`relative rounded-2xl border ${plan.borderClass} ${plan.cardBg} ${plan.glowClass} p-6 flex flex-col`}
              >
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
                    <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold shadow-lg ${plan.badgeColor}`}>
                      <Star size={12} fill="currentColor" />
                      {plan.badge}
                    </span>
                  </div>
                )}
                <h3 className="text-base font-bold text-white mb-1 mt-1">{plan.name}</h3>
                <div className="mb-4 flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">${plan.monthly}</span>
                  <span className="text-sm text-gray-500">/mo</span>
                </div>
                <div className={`inline-flex items-center gap-2 self-start px-3.5 py-2 rounded-full border mb-5 ${plan.subPillClass}`}>
                  <Users size={13} />
                  <span className="text-sm font-bold">{plan.subAccounts}</span>
                  <span className="text-xs font-medium opacity-80">{plan.subLabel}</span>
                </div>
                <div className="mb-5 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Voice Rate</span>
                    <span data-testid={`compare-${plan.id}-voice-starting-rate`} className="text-xs text-teal-400 font-medium">Starting at $0.12/min</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Chat Rate</span>
                    <span className="text-xs text-teal-400 font-medium">$0.025/msg</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">AI Agents</span>
                    <span className="text-xs text-white font-medium">Unlimited</span>
                  </div>
                </div>
                <div className="h-px bg-white/[0.06] mb-5" />
                <div className="flex-1">
                  <p className="text-[10px] tracking-[0.18em] uppercase font-semibold text-gray-500 mb-3">
                    What&apos;s included
                  </p>
                  <ul className="space-y-2.5">
                    {featuresFor(plan.id).map(({ label, highlight }) => (
                      <li key={label} className="flex items-start gap-2.5">
                        <Check size={14} className={`${plan.checkColor} mt-0.5 flex-shrink-0`} />
                        <span className={`text-xs ${highlight ? 'text-white font-semibold' : 'text-gray-300'}`}>{label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Balance is purchased separately. No contracts. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  );
}
